import React, { useState, useEffect, useCallback } from 'react';
const API = '/api';
const BRAND = { olive:'#556B2F', cream:'#F2EFE7', gold:'#A8893D', brown:'#3B2A21', red:'#8F2E24' };
const STORAGE_KEY = (token) => 'bloans_guarantor_' + token;

export default function GuarantorForm({ token }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState({ passport_photo: null, id_document: null, employment_proof: null });
  const [uploadProgress, setUploadProgress] = useState({});
  const TOTAL_STEPS = 5;

  const [form, setForm] = useState({
    title:'', surname:'', first_name:'', other_names:'',
    date_of_birth:'', nationality:'Nigerian', state_of_origin:'', lga:'',
    residential_address:'', nearest_bus_stop:'', closest_landmark:'',
    phone:'', email:'', religion:'',
    relationship:'', years_known:'',
    employment_status:'', occupation:'', employer:'',
    employer_address:'', employer_phone:'', official_email:'',
    position:'', years_in_role:'', registration_number:'',
    id_type:'', id_number:'', id_issuer:'', id_expiry:'',
    witness_name:'', witness_address:'', witness_phone:'',
    witness_occupation:'', witness_relationship:'',
    consent: false, certified: false,
  });

  // Load saved data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY(token));
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setForm(f => ({...f, ...parsed.form}));
        if (parsed.step) setStep(parsed.step);
      } catch(e) {}
    }
  }, [token]);

  useEffect(() => {
    fetch(`${API}/portal/guarantor/${token}`)
      .then(r=>r.json())
      .then(d=>{ if(d.error) setError(d.error); else { setData(d); setForm(f=>({...f, relationship: f.relationship||d.relationship||'' })); } })
      .catch(()=>setError('Failed to load form. Please try again.'))
      .finally(()=>setLoading(false));
  }, [token]);

  // Auto-save to localStorage
  const saveProgress = useCallback(() => {
    localStorage.setItem(STORAGE_KEY(token), JSON.stringify({ form, step }));
    setAutoSaved(true);
    setTimeout(() => setAutoSaved(false), 2000);
  }, [form, step, token]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleFileChange = (fieldname, file) => {
    if (!file) return;
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { setError('Only PDF, JPG and PNG files are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('File size must be under 5MB'); return; }
    setFiles(f => ({...f, [fieldname]: file}));
    setError('');
  };

  const uploadFiles = async () => {
    const hasFiles = Object.values(files).some(f => f !== null);
    if (!hasFiles) return true;
    const formData = new FormData();
    if (files.passport_photo) formData.append('passport_photo', files.passport_photo);
    if (files.id_document) formData.append('id_document', files.id_document);
    if (files.employment_proof) formData.append('employment_proof', files.employment_proof);
    try {
      const res = await fetch(`${API}/portal/guarantor/${token}/upload`, {
        method: 'POST', body: formData
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error); return false; }
      return true;
    } catch(e) { setError('File upload failed. Please try again.'); return false; }
  };

  const submit = async () => {
    const required = ['surname','first_name','date_of_birth','residential_address',
      'phone','email','occupation','employer','relationship','years_known','id_type','id_number'];
    for (const f of required) {
      if (!form[f]) { setError('Please fill in: ' + f.replace(/_/g,' ')); return; }
    }
    if (!form.consent) { setError('Please confirm your consent to proceed'); return; }
    if (!form.certified) { setError('Please certify that all information is true and correct'); return; }
    if (!files.passport_photo) { setError('Please upload your passport photograph — this is required'); return; }
    if (!files.id_document) { setError('Please upload your means of identification (NIN slip, National ID, Drivers licence or International Passport) — this is required'); return; }
    setSaving(true);
    try {
      const uploaded = await uploadFiles();
      if (!uploaded) { setSaving(false); return; }
      const res = await fetch(`${API}/portal/guarantor/${token}/submit`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          full_name: [form.surname, form.first_name, form.other_names].filter(Boolean).join(' '),
          email: form.email, phone: form.phone, address: form.residential_address,
          occupation: form.occupation, employer: form.employer,
          relationship: form.relationship, years_known: form.years_known,
          nin: form.id_number, consent: form.consent, ...form
        })
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error); return; }
      localStorage.removeItem(STORAGE_KEY(token));
      setSubmitted(true);
    } catch(e) { setError('Something went wrong. Please try again.'); }
    finally { setSaving(false); }
  };

  const inputStyle = {width:'100%',padding:'10px 14px',border:'1px solid #E8E0D4',
    borderRadius:8,fontSize:13,fontFamily:'Georgia,serif',color:BRAND.brown,
    background:'#fff',boxSizing:'border-box'};
  const labelStyle = {display:'block',fontSize:11,fontWeight:600,color:'#8A8A8A',
    textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5};
  const sectionHdr = (n, title) => (
    <div style={{background:BRAND.olive,color:BRAND.cream,padding:'10px 16px',
      borderRadius:6,fontWeight:700,fontSize:12,textTransform:'uppercase',
      letterSpacing:'.08em',margin:'24px 0 16px'}}>
      Section {n}: {title}
    </div>
  );
  const field = (label, key, type='text', options=null) => (
    <div style={{marginBottom:14}}>
      <label style={labelStyle}>{label}</label>
      {options ? (
        <select style={inputStyle} value={form[key]} onChange={e=>set(key,e.target.value)}>
          <option value="">Select...</option>
          {options.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
      ) : type==='textarea' ? (
        <textarea style={{...inputStyle,minHeight:70}} value={form[key]} onChange={e=>set(key,e.target.value)} />
      ) : (
        <input type={type} style={inputStyle} value={form[key]} onChange={e=>set(key,e.target.value)} />
      )}
    </div>
  );

  const fileField = (label, fieldname, hint) => (
    <div style={{marginBottom:18}}>
      <label style={labelStyle}>{label}</label>
      <div style={{border:'2px dashed '+(files[fieldname]?BRAND.olive:'#E8E0D4'),borderRadius:8,
        padding:16,textAlign:'center',cursor:'pointer',background:files[fieldname]?'#E8F5EB':'#F9F8F5',
        transition:'all .2s'}}
        onClick={()=>document.getElementById('file_'+fieldname).click()}>
        <input type="file" id={'file_'+fieldname} style={{display:'none'}}
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={e=>handleFileChange(fieldname, e.target.files[0])} />
        {files[fieldname] ? (
          <div>
            <div style={{fontSize:24,marginBottom:4}}>✅</div>
            <div style={{fontSize:12,fontWeight:600,color:BRAND.olive}}>{files[fieldname].name}</div>
            <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{(files[fieldname].size/1024).toFixed(0)} KB</div>
            <div style={{fontSize:11,color:BRAND.olive,marginTop:4}}>Click to replace</div>
          </div>
        ) : (
          <div>
            <div style={{fontSize:32,marginBottom:6}}>📎</div>
            <div style={{fontSize:12,fontWeight:600,color:'#8A8A8A'}}>Click to upload {label}</div>
            <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>PDF, JPG or PNG · Max 5MB</div>
          </div>
        )}
      </div>
      {hint && <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>{hint}</div>}
    </div>
  );

  if (loading) return (
    <div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:BRAND.olive}}>Loading your form...</div>
    </div>
  );

  if (submitted) return (
    <div style={{maxWidth:560,margin:'40px auto',padding:24,textAlign:'center'}}>
      <div style={{background:'#fff',borderRadius:16,padding:40,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{fontSize:56,marginBottom:16}}>✅</div>
        <h2 style={{fontFamily:'Georgia,serif',color:BRAND.olive,marginBottom:12}}>Form Submitted</h2>
        <p style={{color:'#555',fontSize:14,lineHeight:1.7}}>
          Thank you for completing the Guarantor Undertaking Form for <strong>{data?.prospect_name}</strong>.
          Bloans HR will review and verify your information and may contact you shortly.
        </p>
        <p style={{color:'#8A8A8A',fontSize:12,marginTop:16}}>You may close this window.</p>
      </div>
    </div>
  );

  if (error && !data) return (
    <div style={{maxWidth:560,margin:'40px auto',padding:24,textAlign:'center'}}>
      <div style={{background:'#fff',borderRadius:16,padding:40,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
        <h2 style={{fontFamily:'Georgia,serif',color:BRAND.red,marginBottom:12}}>{error}</h2>
        <p style={{color:'#8A8A8A',fontSize:13}}>Contact HR at <a href="mailto:hr@bloanshandbags.com" style={{color:BRAND.olive}}>hr@bloanshandbags.com</a></p>
      </div>
    </div>
  );

  return (
    <div style={{maxWidth:820,margin:'0 auto',padding:'24px 16px',fontFamily:'Georgia,serif'}}>
      {/* Header */}
      <div style={{background:BRAND.brown,borderRadius:10,padding:'16px 20px',marginBottom:16}}>
        <div style={{color:BRAND.gold,fontSize:14,fontWeight:700}}>Guarantor Undertaking Form</div>
        <div style={{color:'rgba(242,239,231,.6)',fontSize:12,marginTop:2}}>
          Bloans Creations Ltd. &nbsp;·&nbsp; Candidate: <strong style={{color:'rgba(242,239,231,.8)'}}>{data?.prospect_name}</strong> &nbsp;·&nbsp; Position: {data?.position}
        </div>
      </div>

      {/* Auto-save notice */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontSize:11,color:'#8A8A8A'}}>
          Your progress is saved automatically. You can close and return to continue.
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {autoSaved && <span style={{fontSize:11,color:BRAND.olive,fontWeight:600}}>✓ Saved</span>}
          <button onClick={saveProgress}
            style={{fontSize:11,padding:'5px 12px',borderRadius:6,border:'1px solid #E8E0D4',
              background:'#fff',cursor:'pointer',color:BRAND.brown}}>
            Save Progress
          </button>
        </div>
      </div>

      {/* Progress */}
      <div style={{background:'#fff',borderRadius:10,padding:'12px 20px',marginBottom:16,
        boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {Array.from({length:TOTAL_STEPS},(_,i)=>(
            <React.Fragment key={i}>
              <div onClick={()=>setStep(i+1)} style={{width:28,height:28,borderRadius:'50%',
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,
                fontWeight:700,flexShrink:0,cursor:'pointer',
                background:i+1<step?BRAND.olive:i+1===step?BRAND.gold:'#E8E0D4',
                color:i+1<=step?'#fff':'#8A8A8A'}}>{i+1}</div>
              {i<TOTAL_STEPS-1 && <div style={{flex:1,height:2,background:i+1<step?BRAND.olive:'#E8E0D4'}}/>}
            </React.Fragment>
          ))}
        </div>
        <div style={{fontSize:11,color:'#8A8A8A',marginTop:8,textAlign:'center'}}>
          {['Important Instructions','Candidate & Relationship','Personal & Employment Details','Documents & Witness','Declaration & Submission'][step-1]}
        </div>
      </div>

      <div style={{background:'#fff',borderRadius:12,padding:28,boxShadow:'0 2px 16px rgba(0,0,0,.06)'}}>

        {step===1 && (
          <div>
            <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,padding:18,marginBottom:20,fontSize:13}}>
              <div style={{fontWeight:700,color:'#8B5E0A',marginBottom:10,textTransform:'uppercase',fontSize:11,letterSpacing:'.08em'}}>Important Instructions</div>
              <ul style={{margin:0,paddingLeft:16,color:'#555',lineHeight:2.2}}>
                <li>You must personally complete and sign this form. The candidate must not complete this form on your behalf.</li>
                <li>You must have known the candidate for at least <strong>3 years</strong>.</li>
                <li>You must be between <strong>30 and 70 years old</strong>.</li>
                <li>Students and unemployed persons are not eligible to stand as guarantors.</li>
                <li>You must provide valid and functional contact details — you will be contacted for verification.</li>
                <li>Kindly respond immediately and within 24 hours once contacted.</li>
                <li>Providing false information may result in rejection of the candidate and referral to appropriate authorities.</li>
              </ul>
            </div>
            <div style={{background:'#F9F8F5',border:'1px solid #E8E0D4',borderRadius:8,padding:16,fontSize:12}}>
              <div style={{fontWeight:700,color:BRAND.brown,marginBottom:10,textTransform:'uppercase',fontSize:11}}>Eligible Guarantor Categories</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:BRAND.olive,color:'#fff'}}>
                  <th style={{padding:'8px 10px',textAlign:'left',width:'40%'}}>Category</th>
                  <th style={{padding:'8px 10px',textAlign:'left'}}>Required Documents</th>
                </tr></thead>
                <tbody>
                  {[
                    ['Civil Servant (Grade Level 7+ or Grade Level 6 with 10+ yrs)', 'Official ID, Passport photo, Driver licence / NIN / International passport'],
                    ['Banker', 'Bank ID, Passport photo, Driver licence / NIN / International passport'],
                    ['Practising Professional (Lawyer, Doctor, Pilot, CA, Engineer)', 'Work ID, Passport photo, Driver licence / NIN / International passport'],
                    ['Lecturer in Reputable Higher Institution', 'Work ID, Passport photo, Driver licence / NIN / International passport'],
                    ['Registered Business Owner (Limited Liability Company)', 'Work ID, Passport photo, CAC/CO7, Driver licence / NIN / International passport'],
                  ].map(([cat,docs],i)=>(
                    <tr key={i} style={{background:i%2===0?'#F9F8F5':'#fff'}}>
                      <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:600,fontSize:11}}>{cat}</td>
                      <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontSize:11,color:'#555'}}>{docs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step===2 && (
          <div>
            {sectionHdr('A', 'Candidate Information')}
            <p style={{fontSize:12,color:'#8A8A8A',marginBottom:16}}>Pre-filled from Bloans records. Please confirm your relationship to the candidate.</p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              <div>
                <label style={labelStyle}>Candidate Full Name</label>
                <input style={{...inputStyle,background:'#F9F8F5',color:'#8A8A8A'}} value={data?.prospect_name||''} readOnly />
              </div>
              <div>
                <label style={labelStyle}>Position Offered</label>
                <input style={{...inputStyle,background:'#F9F8F5',color:'#8A8A8A'}} value={data?.position||''} readOnly />
              </div>
              {field('Your Relationship to Candidate','relationship','text',
                ['Parent','Sibling','Friend','Colleague','Employer','Neighbour','Church/Mosque Member','Other'])}
              {field('Number of Years Known *','years_known','number')}
            </div>
          </div>
        )}

        {step===3 && (
          <div>
            {sectionHdr('B', 'Your Personal Information')}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {field('Title','title','text',['Mr','Mrs','Miss','Ms','Dr','Prof','Engr','Barr','Chief','Alhaji','Alhaja'])}
              {field('Surname *','surname')}
              {field('First Name *','first_name')}
              {field('Other Names','other_names')}
              {field('Date of Birth *','date_of_birth','date')}
              {field('Religion','religion','text',['Christianity','Islam','Traditional','Other'])}
              {field('Nationality','nationality')}
              {field('State of Origin','state_of_origin')}
              {field('Local Government Area','lga')}
            </div>
            {field('Residential Address *','residential_address','textarea')}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {field('Nearest Bus Stop','nearest_bus_stop')}
              {field('Closest Landmark','closest_landmark')}
              {field('Mobile Telephone *','phone')}
              {field('Personal Email *','email','email')}
            </div>
            {sectionHdr('C', 'Employment or Business Information')}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {field('Employment Status','employment_status','text',['Employed','Self-Employed','Business Owner','Retired','Other'])}
              {field('Occupation / Profession *','occupation')}
              {field('Employer / Business Name *','employer')}
              {field('Position / Designation','position')}
              {field('Years in Current Role','years_in_role','number')}
              {field('Official Phone','employer_phone')}
              {field('Official Email','official_email','email')}
              {field('Professional / Business Reg. No.','registration_number')}
            </div>
            {field('Office / Business Address','employer_address','textarea')}
          </div>
        )}

        {step===4 && (
          <div>
            {sectionHdr('D', 'Identification Documents')}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {field('Means of Identification *','id_type','text',['National ID Card','Driver Licence','International Passport','Voter Card','Staff ID Card'])}
              {field('Identification Number *','id_number')}
              {field('Issuing Authority','id_issuer')}
              {field('Expiry Date (if applicable)','id_expiry','date')}
            </div>

            <div style={{margin:'20px 0'}}>
              <div style={{fontWeight:700,fontSize:12,color:BRAND.brown,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:12}}>Upload Documents</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>
                {fileField('Passport Photograph *', 'passport_photo', 'Clear recent photo, signed at back')}
                {fileField('Means of Identification *', 'id_document', 'Must match details above')}
                {fileField('Employment / Business Proof', 'employment_proof', 'Work ID, appointment letter or CAC document')}
              </div>
            </div>

            {sectionHdr('G', 'Witness Information')}
            <p style={{fontSize:12,color:'#8A8A8A',marginBottom:16}}>Optional but recommended — details of a witness to your signature.</p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {field('Witness Full Name','witness_name')}
              {field('Relationship to You','witness_relationship')}
              {field('Witness Phone','witness_phone')}
              {field('Witness Occupation','witness_occupation')}
            </div>
            {field('Witness Address','witness_address','textarea')}
          </div>
        )}

        {step===5 && (
          <div>
            {sectionHdr('E & F', 'Declaration, Undertaking and Consent')}
            <div style={{background:'#F9F8F5',border:'1px solid #E8E0D4',borderRadius:8,
              padding:20,fontSize:13,lineHeight:1.9,color:BRAND.brown,marginBottom:20}}>
              <p style={{margin:'0 0 12px'}}>I, <strong>{[form.surname,form.first_name,form.other_names].filter(Boolean).join(' ')||'[Your Name]'}</strong>, declare that the information and documents supplied in this form are true, accurate and complete.</p>
              <p style={{margin:'0 0 12px'}}>I confirm that I have known <strong>{data?.prospect_name}</strong> for approximately <strong>{form.years_known||'—'}</strong> years and, to the best of my knowledge, the candidate is suitable to be considered for employment by Bloans Creations Ltd.</p>
              <p style={{margin:'0 0 12px'}}>I understand that Bloans may contact me to verify this undertaking. I undertake to encourage the candidate to act honestly, responsibly and in accordance with the lawful terms of employment.</p>
              <p style={{margin:'0 0 12px'}}>If Bloans is unable to contact the candidate during an unexplained absence, suspected abandonment of duty or another serious employment matter, I consent to Bloans contacting me for assistance in reaching the candidate.</p>
              <p style={{margin:'0 0 12px'}}>I understand that I do not become responsible for ordinary errors, poor performance or routine workplace losses merely by signing this form. Any personal liability attributed to me must arise only from a separate clear legal basis or a final decision of a court or competent authority.</p>
              <p style={{margin:0}}>I agree to notify Bloans if my contact details, employment or business information materially changes while this guarantee remains relevant.</p>
            </div>
            <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer',marginBottom:14}}>
              <input type="checkbox" style={{accentColor:BRAND.olive,width:18,height:18,flexShrink:0,marginTop:2}}
                checked={form.consent} onChange={e=>set('consent',e.target.checked)} />
              <span style={{fontSize:13,color:BRAND.brown,lineHeight:1.7}}>
                I consent to Bloans Creations Ltd. collecting, using, storing and verifying my personal information for candidate screening, employment administration, legal compliance and the protection of Bloans legitimate interests.
              </span>
            </label>
            <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer',marginBottom:24}}>
              <input type="checkbox" style={{accentColor:BRAND.olive,width:18,height:18,flexShrink:0,marginTop:2}}
                checked={form.certified} onChange={e=>set('certified',e.target.checked)} />
              <span style={{fontSize:13,color:BRAND.brown,lineHeight:1.7}}>
                I certify that all information I have provided in this form is true, accurate and complete to the best of my knowledge. I understand that providing false information may result in rejection of the candidate and referral to the appropriate authorities.
              </span>
            </label>
            {error && <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
              padding:'10px 16px',marginBottom:16,fontSize:13,color:BRAND.red}}>{error}</div>}
          </div>
        )}

        {/* Navigation */}
        <div style={{display:'flex',justifyContent:'space-between',marginTop:28,
          paddingTop:20,borderTop:'1px solid #E8E0D4'}}>
          {step>1 ? (
            <button onClick={()=>{setError('');saveProgress();setStep(s=>s-1);}}
              style={{padding:'10px 24px',borderRadius:8,border:'1px solid #E8E0D4',
                background:'#fff',cursor:'pointer',fontSize:14,color:BRAND.brown}}>
              ← Previous
            </button>
          ) : <div/>}
          <div style={{display:'flex',gap:8}}>
            {step<TOTAL_STEPS && (
              <button onClick={saveProgress}
                style={{padding:'10px 18px',borderRadius:8,border:'1px solid #E8E0D4',
                  background:'#fff',cursor:'pointer',fontSize:13,color:BRAND.brown}}>
                💾 Save
              </button>
            )}
            {step<TOTAL_STEPS ? (
              <button onClick={()=>{setError('');saveProgress();setStep(s=>s+1);}}
                style={{padding:'10px 28px',borderRadius:8,border:'none',
                  background:BRAND.olive,color:'#fff',cursor:'pointer',fontSize:14,fontWeight:600}}>
                Next →
              </button>
            ) : (
              <button onClick={submit} disabled={saving}
                style={{padding:'10px 28px',borderRadius:8,border:'none',
                  background:saving?'#ccc':BRAND.olive,color:'#fff',
                  cursor:saving?'not-allowed':'pointer',fontSize:14,fontWeight:700}}>
                {saving?'Submitting...':'Submit Guarantor Form'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
